// base-price.ts — pricing model: sum of components.
//
// A print line's price is not all-inclusive: it adds up as bare material + print + cutting +
// lamination + finishings, each component carrying its own €/m². A 0 € component is transparent,
// and a material used as a finishing contributes its own PV. This is the full catalogue price of
// the line, before tiers, discounts and minima.

import { STD_PRINT_MAP, COMBINATIONS } from "../../data";
import { ACTIVE_CATALOGUE, type Catalogue } from "./catalogue-source";
import type { Material } from "../../models/data.model";
import { type QuoteLine, num, findMat } from "../core/core";

/**
 * PV €/m² of a component looked up by code; 0 when absent, so it stays transparent.
 * @param list The component list to search.
 * @param code The component code.
 * @returns The component PV €/m², or 0.
 */
export function compPvOf(list: readonly { code: string; pv: number }[], code: string | undefined): number {
  return list.find((x) => x.code === code)?.pv ?? 0;
}

/**
 * PV €/m² of a finishing: looked up in the per-m² table then the per-linear-metre one; failing
 * that, a material ref contributes its own PV. 0 when nothing matches.
 * @param code The finishing code (or a material ref used as a finishing).
 * @param cat The catalogue.
 * @returns The finishing PV €/m².
 */
export function finishingPv(code: string | undefined, cat: Catalogue = ACTIVE_CATALOGUE): number {
  if (!code) return 0;
  const c = cat.finishings.find((x) => x.code === code) ?? cat.finishingsMl.find((x) => x.code === code);
  if (c) return c.pv;
  const m = cat.materials.find((x) => x.ref === code && x.pv != null); // material used as a finishing
  return m ? num(m.pv) : 0;
}

/**
 * Standard print ref of a material: maps the `standardPrint` label (e.g. "UV GEL") to a print ref via
 * STD_PRINT_MAP. Empty string when there is none. Used to preselect the print.
 * @param materialEntry The material.
 * @returns The standard print ref, or an empty string.
 */
export function stdPrintRef(materialEntry: Material | undefined): string {
  if (!materialEntry || !materialEntry.standardPrint) return "";
  const k = String(materialEntry.standardPrint).toUpperCase().replace(/-/g, " ").replace(/\s+/g, " ").trim();
  return STD_PRINT_MAP[k] ?? "";
}

/**
 * Full catalogue price of the line (€/m²): bare material + print + cutting + lamination +
 * finishings 1 to 6. Returns 0 when the material is unknown.
 * @param l The quote line.
 * @param cat The catalogue.
 * @returns The full price €/m².
 */
export function pvComponents(l: QuoteLine, cat: Catalogue = ACTIVE_CATALOGUE): number {
  const materialEntry = findMat(l.material, cat);
  if (!materialEntry) return 0;
  const printPv = l.print ? compPvOf(cat.prints, l.print) : 0;
  let finishingsTotal = 0;
  for (const c of [l.finishing1, l.finishing2, l.finishing3, l.finishing4, l.finishing5, l.finishing6]) finishingsTotal += finishingPv(c, cat);
  return num(materialEntry.pv) + printPv + compPvOf(cat.cuts, l.cut) + compPvOf(cat.laminations, l.lamination) + finishingsTotal;
}

// Per-component breakdown, used by the dedicated client grid

/** A component of the full PV, with its role and ref: the base of per-component resolution. */
export interface PvComponent {
  role: "material" | "print" | "cut" | "lamination" | "finishing";
  /** Finishing index (1..6), absent outside finishings. */
  finishingIndex?: number;
  code: string;
  pv: number;
}

/**
 * Breaks the line's full PV down into its components. Invariant: the sum of the components equals
 * `pvComponents`, so the dedicated grid discounts each component without reinventing the base.
 * @param l The quote line.
 * @param cat The catalogue.
 * @returns The components of the full PV.
 */
export function splitPv(l: QuoteLine, cat: Catalogue = ACTIVE_CATALOGUE): PvComponent[] {
  const materialEntry = findMat(l.material, cat);
  if (!materialEntry) return [];
  const out: PvComponent[] = [{ role: "material", code: materialEntry.ref, pv: num(materialEntry.pv) }];
  if (l.print) out.push({ role: "print", code: l.print, pv: compPvOf(cat.prints, l.print) });
  if (l.cut) out.push({ role: "cut", code: l.cut, pv: compPvOf(cat.cuts, l.cut) });
  if (l.lamination) out.push({ role: "lamination", code: l.lamination, pv: compPvOf(cat.laminations, l.lamination) });
  const finishings = [l.finishing1, l.finishing2, l.finishing3, l.finishing4, l.finishing5, l.finishing6];
  finishings.forEach((c, i) => {
    if (c) out.push({ role: "finishing", finishingIndex: i + 1, code: c, pv: finishingPv(c, cat) });
  });
  return out;
}

/**
 * Does the line contain a registered combination's signature? Exact match on material, print,
 * cutting and lamination, and the combination's finishings must be present on the line. Extra
 * components beyond the signature do not break the match, so the negotiated rate still applies.
 * @param l The quote line.
 * @param ref The combination reference.
 * @returns True when the line contains the combination's signature.
 */
export function lineContainsCombination(l: QuoteLine, ref: string): boolean {
  const c = COMBINATIONS.find((x) => x.ref === ref);
  if (!c) return false;
  if ((l.material ?? "") !== (c.material ?? "")) return false;
  if ((l.print ?? "") !== (c.print ?? "")) return false;
  if ((l.cut ?? "") !== (c.cut ?? "")) return false;
  if ((l.lamination ?? "") !== (c.lamination ?? "")) return false;
  const lineFinishings = [l.finishing1, l.finishing2, l.finishing3, l.finishing4, l.finishing5, l.finishing6].filter(Boolean) as string[];
  for (const cc of [c.finishing1, c.finishing2].filter(Boolean) as string[]) {
    if (!lineFinishings.includes(cc)) return false;
  }
  return true;
}

/**
 * Full catalogue price of a registered combination (€/m²), the base for converting a grid entry
 * into euros on a combination target. Composed by the same component sum as lines. 0 if unknown.
 * @param ref The combination reference.
 * @param cat The catalogue.
 * @returns The full combination price €/m², or 0.
 */
export function fullCombination(ref: string | null | undefined, cat: Catalogue = ACTIVE_CATALOGUE): number {
  if (!ref) return 0;
  const c = COMBINATIONS.find((x) => x.ref === ref);
  if (!c) return 0;
  const pseudo: QuoteLine = {
    type: "PRINT_GF",
    material: c.material,
    print: c.print,
    cut: c.cut,
    lamination: c.lamination,
    finishing1: c.finishing1,
    finishing2: c.finishing2,
  } as QuoteLine;
  return pvComponents(pseudo, cat);
}
